import HeroSection from "@/components/HeroSection";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Users, ArrowRight, Heart, BookOpen, Gift, Handshake } from "lucide-react";

const JR_IMAGE = "https://customer-assets.emergentagent.com/job_trauma-youth-digital/artifacts/xd7alt8t_Screenshot%202026-05-10%20at%2010.03.58%E2%80%AFAM.png";

const PROGRAMS = [
  { id: "caregiver-boxes", icon: Gift, title: "Caregiver Boxes", desc: "Self-care BroglieBoxes thoughtfully curated to support the mental and emotional well-being of caregivers of youth in crisis.", image: "https://customer-assets.emergentagent.com/job_trauma-youth-digital/artifacts/sbvhcm8u_1.jpg", link: "/caregiver-boxes" },
  { id: "caregiver", icon: BookOpen, title: "Caregiver Resources", desc: "Local and national mental health resources connecting caregivers to support, crisis lines, and peer networks.", image: "https://images.unsplash.com/photo-1758687127405-f45f90509902?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjYXJlZ2l2ZXIlMjB0ZWVuYWdlciUyMHN1cHBvcnR8ZW58MHx8fHwxNzc2MDEwNjEzfDA&ixlib=rb-4.1.0&q=85", link: "/caregiver-resources" },
  { id: "jays-corner", icon: Heart, title: "Jay's Corner", desc: "A safe space for youth — peer stories, coping tools, and gentle encouragement for every young person.", image: JR_IMAGE, imagePosition: "center 30%", link: "/jays-corner" },
  { id: "community", icon: Handshake, title: "Community Partnerships", desc: "Building meaningful partnerships with local organizations to expand support for families.", image: "https://customer-assets.emergentagent.com/job_trauma-youth-digital/artifacts/5av5v5lg_closeup-two-business-women-shaking-hands-business-agreement-concept.jpg", imagePosition: "center", link: "/community-partnerships" },
];

const STATS = [
  { number: "1 in 5", label: "Youth Affected", desc: "One in five children in the U.S. experience a mental health condition." },
  { number: "70%", label: "Caregivers Overwhelmed", desc: "The majority of caregivers feel overwhelmed and isolated during their child's crisis." },
  { number: "50%", label: "Go Untreated", desc: "Half of all mental health conditions begin by age 14. Early intervention changes everything." },
];

function useScrollReveal(ref) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.querySelectorAll(".animate-on-scroll, .slide-left, .slide-right, .scale-in, .blur-in, .stagger-item, .line-animate").forEach((el) => el.classList.add("visible"));
      }),
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
}

export default function HomePage() {
  const aboutRef = useRef(null);
  const statsRef = useRef(null);
  const programsRef = useRef(null);
  const donationRef = useRef(null);
  const ctaRef = useRef(null);
  useScrollReveal(aboutRef);
  useScrollReveal(statsRef);
  useScrollReveal(programsRef);
  useScrollReveal(donationRef);
  useScrollReveal(ctaRef);

  return (
    <main>
      <HeroSection />

      {/* Quick About */}
      <section id="home-about" ref={aboutRef} className="py-24 md:py-32 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className="animate-on-scroll">
              <p className="section-label text-xs font-sans tracking-[0.2em] uppercase font-semibold text-earth-muted mb-4">Who We Are</p>
              <h2 data-testid="home-about-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-tight text-earth mb-6">
                Built on Love,<br /><span className="gradient-text">Driven by Need</span>
              </h2>
              <p className="text-base sm:text-lg font-sans font-medium leading-relaxed text-earth-muted mb-8">
                TYSS was born from lived experience — from a caregiver with over 20 years in mission-driven nonprofits, who is also a parent of two children adopted from foster care with complex mental health challenges. We exist to ensure no family navigates crisis alone.
              </p>
              <Link to="/about" data-testid="home-about-link" className="inline-flex items-center gap-2 text-sm font-sans font-semibold text-[#0E8DB5] hover:text-[#0A7599] transition-colors no-underline">
                Learn Our Full Story <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="animate-on-scroll delay-200 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="p-6 rounded-2xl bg-white border border-[#C8DDD8]/50 hover:border-[#0E8DB5]/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <Target className="w-8 h-8 text-[#0E8DB5] mb-3 icon-float" />
                  <h3 className="text-lg font-serif font-bold text-earth mb-1">Mission</h3>
                  <p className="text-sm font-sans text-earth-muted">Supporting and empowering families through crisis with compassion and real resources.</p>
                </div>
                <div className="p-6 rounded-2xl bg-white border border-[#C8DDD8]/50 hover:border-[#3DAA35]/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <Users className="w-8 h-8 text-[#3DAA35] mb-3 icon-float" />
                  <h3 className="text-lg font-serif font-bold text-earth mb-1">Who We Serve</h3>
                  <p className="text-sm font-sans text-earth-muted">Caregivers and families navigating youth mental health crises.</p>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="p-6 rounded-2xl bg-white border border-[#C8DDD8]/50 hover:border-[#0E8DB5]/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <Eye className="w-8 h-8 text-[#0E8DB5] mb-3 icon-float" />
                  <h3 className="text-lg font-serif font-bold text-earth mb-1">Vision</h3>
                  <p className="text-sm font-sans text-earth-muted">A future where no family stands alone in crisis.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section ref={statsRef} className="py-24 md:py-32 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="animate-on-scroll text-xs font-sans tracking-[0.2em] uppercase font-semibold text-earth-muted mb-4 flex items-center justify-center">
            <span className="inline-block w-8 h-[2px] bg-[#0E8DB5] mr-3" />The Crisis Is Now<span className="inline-block w-8 h-[2px] bg-[#0E8DB5] ml-3" />
          </p>
          <h2 data-testid="home-stats-heading" className="animate-on-scroll text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-earth mb-16">
            Why This <span className="gradient-text">Matters</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STATS.map((stat, i) => (
              <div key={stat.label} data-testid={`home-stat-${i}`} className={`animate-on-scroll delay-${(i+1)*100} text-center p-10 rounded-2xl bg-white border border-[#C8DDD8]/50 hover:border-[#0E8DB5]/30 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 group`}>
                <p className="text-5xl sm:text-6xl font-serif font-bold shimmer-text mb-3">{stat.number}</p>
                <p className="text-sm font-sans tracking-[0.15em] uppercase font-semibold text-earth mb-4 group-hover:text-[#0E8DB5] transition-colors">{stat.label}</p>
                <p className="text-sm font-sans leading-relaxed text-earth-muted">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section ref={programsRef} className="py-24 md:py-32 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="animate-on-scroll mb-16">
            <p className="section-label text-xs font-sans tracking-[0.2em] uppercase font-semibold text-earth-muted mb-4">What We Do</p>
            <h2 data-testid="home-programs-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-earth">
              Programs & <span className="gradient-text">Services</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROGRAMS.map((prog, i) => (
              <Link key={prog.id} to={prog.link} data-testid={`home-program-${prog.id}`} className={`animate-on-scroll delay-${(i+1)*100} bento-card rounded-2xl bg-white border border-[#C8DDD8]/50 overflow-hidden group no-underline`}>
                <div className="h-48 overflow-hidden relative">
                  <img src={prog.image} alt={prog.title} className="w-full h-full object-cover" style={{ objectPosition: prog.imagePosition || "center" }} loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E8DB5]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-[#0E8DB5]/10 flex items-center justify-center group-hover:bg-[#0E8DB5] transition-colors duration-300">
                      <prog.icon className="w-4 h-4 text-[#0E8DB5] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-lg font-serif font-bold text-earth">{prog.title}</h3>
                  </div>
                  <p className="text-sm font-sans text-earth-muted leading-relaxed mb-4">{prog.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-sans font-semibold text-[#0E8DB5]">
                    Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Donation */}
      <section ref={donationRef} className="py-24 md:py-32 bg-[#1A3A4A] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#0E8DB5]/10 rounded-full translate-x-1/2 -translate-y-1/2 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#3DAA35]/10 rounded-full -translate-x-1/3 translate-y-1/3 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <p className="animate-on-scroll text-xs font-sans tracking-[0.2em] uppercase font-semibold text-[#7CC8E0] mb-4">
            <span className="inline-block w-8 h-[2px] bg-[#0E8DB5] mr-3 align-middle" />Make a Difference
          </p>
          <h2 data-testid="home-donation-heading" className="animate-on-scroll delay-100 text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight mb-6">
            Your Support <span className="text-[#7CC8E0]">Changes Lives</span>
          </h2>
          <p className="animate-on-scroll delay-200 text-base sm:text-lg font-sans font-medium text-white/80 max-w-2xl mx-auto mb-10">
            Every donation provides caregivers with essential resources, self-care tools, and a compassionate network. You're telling a family in crisis: <em className="text-white">"You are not alone."</em>
          </p>
          <div className="animate-on-scroll delay-300 flex flex-wrap justify-center gap-4 mb-8">
            {["$25", "$50", "$100"].map((amt) => (
              <a key={amt} href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a" target="_blank" rel="noopener noreferrer" data-testid={`home-donate-${amt.replace("$","")}`}
                className="w-24 py-3 rounded-xl text-center font-sans font-semibold text-white border border-white/20 hover:bg-[#0E8DB5] hover:border-[#0E8DB5] transition-all duration-300 no-underline hover:-translate-y-1">
                {amt}
              </a>
            ))}
          </div>
          <a data-testid="home-donate-main" href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a" target="_blank" rel="noopener noreferrer"
            className="btn-pill btn-primary text-base px-10 py-4 no-underline shadow-lg shadow-[#0E8DB5]/30 inline-flex">
            <Heart className="w-5 h-5" /> Donate Now
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ctaRef} className="py-24 md:py-32 bg-[#F8FAFB] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#0E8DB5]/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#3DAA35]/5 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <div className="animate-on-scroll">
            <div className="w-16 h-16 rounded-full bg-[#0E8DB5]/10 flex items-center justify-center mx-auto mb-8 icon-float">
              <Heart className="w-8 h-8 text-[#0E8DB5]" />
            </div>
            <h2 data-testid="home-cta-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-earth mb-6">
              No Family Should Face<br /><span className="gradient-text">Crisis Alone</span>
            </h2>
          </div>
          <p className="animate-on-scroll delay-100 text-base sm:text-lg font-sans font-medium text-earth-muted max-w-2xl mx-auto mb-10">
            Right now, a parent is wondering if their child will be okay. They need to know someone cares. That someone is <strong className="text-earth">you</strong>.
          </p>
          <div className="animate-on-scroll delay-200 flex flex-wrap justify-center gap-4">
            <a href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a" target="_blank" rel="noopener noreferrer" data-testid="home-final-donate"
              className="btn-pill btn-primary text-base px-10 py-4 no-underline shadow-lg shadow-[#0E8DB5]/30">
              <Heart className="w-5 h-5" /> Donate Now
            </a>
            <Link to="/caregiver-resources" data-testid="home-final-help" className="btn-pill btn-green text-base px-10 py-4 no-underline">
              Get Help
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
